/**
 * Directory card for a public Space (search + Spaces directory).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { PublicSpace } from '@adieuu/shared';
import { useCipherStore } from '../hooks/useCipherStore';
import { useIdentity } from '../hooks/useIdentity';
import { useSpaces } from '../hooks/useSpaces';
import { getSpaceCipherLink } from '../services/spaceCipherService';
import { canBrowseSpace } from '../pages/spaces/joinSpaceGate';
import { JoinSpaceInterstitial } from '../pages/spaces/JoinSpaceInterstitial';
import {
  resolveSpaceDisplayDescription,
  resolveSpaceDisplayName,
} from '../pages/spaces/spaceMetadataCipher';
import { AliasSignInPrompt } from './AliasSignInPrompt';
import { Button } from './Button';
import { Card } from './Card';
import { SpaceLogo } from './SpaceLogo';
import '../styles/_spaces.scss';

export interface SpaceCardProps {
  space: PublicSpace;
  className?: string;
}

export function SpaceCard({ space, className = '' }: SpaceCardProps) {
  const { t } = useTranslation();
  const { status: identityStatus } = useIdentity();
  const isLoggedIn = identityStatus === 'logged_in';
  const { spaces: joinedSpaces, lurkedSpaces = [] } = useSpaces();
  const { getCipherKey } = useCipherStore();
  const [joinOpen, setJoinOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);

  const localCipherId = getSpaceCipherLink(space.id);
  const cipher = localCipherId ? getCipherKey(localCipherId) : null;
  const displayName = resolveSpaceDisplayName(space, cipher, {
    encryptedSpace: t('spaces.encryptedSpacePlaceholder'),
  });
  const description = resolveSpaceDisplayDescription(space, cipher);
  const spacePath = `/s/${space.slug}`;
  const isBanned = space.membershipStatus === 'banned';
  const isJoined =
    !isBanned &&
    (space.membershipStatus === 'active' || joinedSpaces.some((s) => s.id === space.id));
  const isLurking = !isBanned && !isJoined && lurkedSpaces.some((s) => s.id === space.id);
  const canBrowse = !isBanned && (isJoined || canBrowseSpace(space));

  const handleJoinClick = () => {
    if (!isLoggedIn) {
      setSignInOpen(true);
      return;
    }
    setJoinOpen(true);
  };

  return (
    <>
      <Card variant="elevated" className={`spaces-card ${className}`.trim()}>
        <div className="spaces-card-header">
          <SpaceLogo space={space} name={displayName} size="lg" className="spaces-card-logo" />
          <div className="spaces-card-identity">
            {canBrowse ? (
              <>
                <Link to={spacePath} className="spaces-card-name">
                  {displayName}
                </Link>
                <Link to={spacePath} className="spaces-card-slug">
                  /s/{space.slug}
                </Link>
              </>
            ) : (
              <>
                <div className="spaces-card-name">{displayName}</div>
                <div className="spaces-card-slug">/s/{space.slug}</div>
              </>
            )}
          </div>
          <div className="spaces-card-badges">
            <span className="spaces-badge">
              {t(`spaces.visibility.${space.visibility}`)}
            </span>
            {space.e2ee && (
              <span className="spaces-badge spaces-badge--encrypted">
                {t('spaces.encrypted')}
              </span>
            )}
            {space.cipherRequired && (
              <span className="spaces-badge">
                {t('spaces.joinModal.cipherRequiredBadge')}
              </span>
            )}
            {isBanned && (
              <span className="spaces-badge spaces-badge--banned">
                {t('spaces.banned', 'Banned')}
              </span>
            )}
            {isJoined && (
              <span className="spaces-badge spaces-badge--joined">
                {t('spaces.joined')}
              </span>
            )}
          </div>
        </div>

        {description && (
          <p className="spaces-card-description">{description}</p>
        )}

        <div className="spaces-card-footer">
          <span className="spaces-card-members">
            {isLurking
              ? t('spaces.lurking')
              : t('spaces.memberCount', { count: space.memberCount })}
          </span>
          <div className="spaces-card-actions">
            {isBanned ? (
              <Button
                variant="secondary"
                size="sm"
                disabled
                aria-label={t('spaces.bannedCannotJoin', 'You are banned from this Space')}
              >
                {t('spaces.banned', 'Banned')}
              </Button>
            ) : isJoined ? (
              <Link
                to={spacePath}
                className="btn btn-primary btn-sm"
                aria-label={`${t('spaces.open')} ${displayName}`}
              >
                {t('spaces.open')}
              </Link>
            ) : (
              <>
                {canBrowseSpace(space) && (
                  <Link
                    to={spacePath}
                    className="btn btn-secondary btn-sm"
                    aria-label={`${t('spaces.joinModal.browse')} ${displayName}`}
                  >
                    {t('spaces.joinModal.browse')}
                  </Link>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleJoinClick}
                  aria-label={`${t('spaces.join')} ${displayName}`}
                >
                  {t('spaces.join')}
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <JoinSpaceInterstitial
        space={joinOpen ? space : null}
        open={joinOpen}
        onOpenChange={(open) => {
          if (!open) setJoinOpen(false);
        }}
      />

      <AliasSignInPrompt
        open={signInOpen}
        onOpenChange={setSignInOpen}
        bodyKey="search.aliasRequiredJoin"
      />
    </>
  );
}
